<script setup lang="ts">
import NamaTimelineBox from "@/components/EditView/NamaTimelineBox.vue";
import {computed, nextTick, onMounted, ref, useTemplateRef} from "vue";
import {useResizeObserver} from "@vueuse/core";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {gsap} from "gsap";
import DTOTimelineData = namaTypes.DTOTimelineData;

//default mode is horizontal
//maxLength must be 24 for verticalMode
const props = defineProps<{
  timeline: DTOTimelineData[],
  isHorizontal?: boolean,
  heading?: string,
  supportSnakeVerticalLayout?: boolean
}>()

const timelineWrapperRef = useTemplateRef<HTMLElement>("timelineWrapperRef")
const timelineContainerRef = useTemplateRef<HTMLElement>("timelineContainerRef")

enum Sizes {
  Zero = 0,
  Xxs = 1,
  Xs = 2,
  Sm = 3,
  Md = 4,
  Lg = 6,
  Xl = 7,
  Xxl = 8
}

const maxColsInRow = ref(Sizes.Md);
const maxRowsInCol = ref(props.isHorizontal ? Sizes.Xxs : Sizes.Md);

const isRowDirectionLtr = computed(() => {
  return (index: number) => {
    const row = Math.floor(index / maxColsInRow.value)
    return row % 2 === 0
  }
})
const isColumnDirectionTopToDown = computed(() => {
  return (index: number) => {
    const column = Math.floor(index / maxRowsInCol.value);
    return column % 2 === 0
  }
})
const minContent = computed(() => props.isHorizontal ? '0px' : "200px")

useResizeObserver(timelineWrapperRef, (entry) => {
  const width = entry[0].contentRect.width;
  if (width < 641)
    maxColsInRow.value = Sizes.Xs;
  else if (width > 640 && width < 768)
    maxColsInRow.value = Sizes.Sm;
  else if (width > 768 && width < 1025)
    maxColsInRow.value = Sizes.Md;
  else if (width > 1025 && width < 1301)
    maxColsInRow.value = Sizes.Lg;
  else if (width > 1300 && width < 1450)
    maxColsInRow.value = Sizes.Xl;
  else if (width > 1450)
    maxColsInRow.value = Sizes.Xxl;
})

function calcHorizontalGridArea(index: number) {
  const row = Math.floor(index / maxColsInRow.value)
  const colInRow = index % maxColsInRow.value

  const isEvenRow = row % 2 === 0
  const col = isEvenRow ? colInRow + 1 : maxColsInRow.value - colInRow

  const rowStart = row + 1
  const colStart = col
  return `${rowStart} / ${colStart} / ${rowStart + 1} / ${colStart + 1}`
}

function calcVerticalGridArea(index: number): string {
  const maxRows = maxRowsInCol.value;

  const col = Math.floor(index / maxRows);
  const posInCol = index % maxRows;
  const isDown = col % 2 === 0;

  const row = isDown ? posInCol + 1 : maxRows - posInCol;
  const colStart = col + 1;

  return `${row} / ${colStart} / ${row + 1} / ${colStart + 1}`;
}

function animate() {
  const container = timelineContainerRef.value
  if (!container)
    return
  gsap.set('[data-timeline-dashboard-widget]', {opacity: 1});
  const timeline = gsap.timeline()
  const boxes: HTMLDivElement[] = container.querySelectorAll("[data-animate-timeline-box]")
  for (let box of boxes) {
    if (!box)
      continue;
    const boxTl = gsap.timeline();
    const fromUp = box.querySelector("[data-animate-from-up]")
    const fromDown = box.querySelector("[data-animate-from-down]")
    const horizontalLine = box.querySelector("[data-animate-timline-horizontal-line]")
    const verticalLine = box.querySelector("[data-animate-timeline-verticalline]")
    const duration = 0.18;
    boxTl.from(box, {
      duration: duration + .01, opacity: 0, scale: 0.95, onComplete() {
        setTimeout(() => {
          box.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        })
      }
    });
    if (fromUp)
      boxTl.from(fromUp, {duration, ease: "power4.inOut", opacity: 0, translateY: -8}, "<")
    if (fromDown)
      boxTl.from(fromDown, {
        duration, ease: "power4.inOut", opacity: 0, translateY: 8
      }, "<")
    if (horizontalLine)
      boxTl.from(horizontalLine, {
        duration, ease: "power4.inOut", width: 0
      }, "<")
    if (verticalLine)
      boxTl.from(verticalLine, {duration, ease: "power4.inOut", height: 0}, "<")
    timeline.add(boxTl);
  }
}

onMounted(async () => {
  await nextTick();
  await NamaCommon.waitFor(250)
  animate();
})

</script>

<template>
  <div ref="timelineContainerRef" data-timeline-dashboard-widget
       class="tw-w-full tw-h-full tw-mx-auto tw-px-4 tw-py-2.5 tw-overflow-x-auto tw-flex-grow">
    <div
      v-if="heading"
      v-text="heading" class="tw-pt-3 tw-font-bold tw-text-2xl md:tw-mb-4 dark:tw-text-white"/>
    <main
      :style="{minWidth:(!isHorizontal) && supportSnakeVerticalLayout? (200/4 * timeline.length  )+'px':'0px'}"
      :class="[{'tw-mx-auto':supportSnakeVerticalLayout},
      {'timeline-grid-wrapper':!(!supportSnakeVerticalLayout && !isHorizontal)},
      {'tw-flex tw-flex-col':!supportSnakeVerticalLayout && !isHorizontal}]"
      id="timeline-wrapper"
      ref="timelineWrapperRef"
      class="tw-h-full">
      <NamaTimelineBox
        :style="{ gridArea: isHorizontal ? calcHorizontalGridArea(index):supportSnakeVerticalLayout?calcVerticalGridArea(index):'' }"
        v-for="(action,index) in timeline"
        :key="index"
        :action="action"
        :isFirstElementInArr="index==0"
        :canShowLines="timeline.length>1"
        :isLastElementInArr="index == timeline.length -1"
        :isRowDirectionLtr="isRowDirectionLtr(index)"
        :isColumnDirectionTopToDown="isColumnDirectionTopToDown(index)"
        :canShowHorizontalLine="(index + 1) % maxColsInRow === 0 && !!isHorizontal"
        :canShowVerticalLine="(index + 1) % maxRowsInCol === 0 && !isHorizontal && !!supportSnakeVerticalLayout"
        :supportSnakeVerticalLayout="supportSnakeVerticalLayout"
        :isHorizontal="isHorizontal"
      />
    </main>
  </div>
</template>

<style scoped>
.timeline-grid-wrapper {
  display: grid;
  grid-template-columns: repeat(v-bind(maxColsInRow), minmax(v-bind(minContent), 1fr));
  grid-template-rows: repeat(v-bind(maxRowsInCol), minmax(min-content, 1fr));
}
</style>
